import { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown } from "lucide-react";
import { TossScoreOrb } from "@/components/TossScoreOrb";
import { WatchlistPinButton } from "@/components/WatchlistPinButton";
import { cn } from "@/lib/utils";

/**
 * Shared token card.
 *
 * One base layout for every place we show a token: TOSS Opportunities,
 * Undervalued Gems and Track Record ledger entries. Context-specific extras
 * (entry/current price and P&L, sector tags, trading footers) are passed in
 * as props rather than forked into separate card components.
 */

export interface TokenCardChange {
  label: string;
  /** Percentage value. Null renders an em dash. */
  value: number | null | undefined;
}

export interface TokenCardStat {
  label: string;
  value: ReactNode;
  /** Optional text colour class for the value. */
  tone?: string;
}

export interface TokenCardProps {
  symbol: string;
  name?: string | null;
  /** Position in a ranked list, shown as a #n chip. */
  rank?: number;
  /** Formatted price string (already currency-formatted by the caller). */
  price?: string | null;
  priceCaption?: string;
  /** 0-100 score rendered as the TOSS orb. */
  score?: number | null;
  /** Text rating/status badge shown next to the title. */
  rating?: ReactNode;
  ratingClassName?: string;
  /** Extra badges beside the title (exchange listings, risk, correlation…). */
  badges?: ReactNode;
  /** Sector / category tags. */
  tags?: string[];
  /** Headline percentage changes, e.g. 24h and 7d. */
  changes?: TokenCardChange[];
  /** Context-specific labelled figures, laid out in a grid. */
  stats?: TokenCardStat[];
  /** Short reasoning / thesis line. */
  reasoning?: ReactNode;
  /** Anything above the footer that only one context needs. */
  children?: ReactNode;
  footer?: ReactNode;
  /** Replaces the score orb in the top-right corner. */
  headerRight?: ReactNode;
  /** Show the watchlist pin/star. Defaults to true. */
  pinnable?: boolean;
  hero?: boolean;
  className?: string;
  onClick?: () => void;
}

const pctTone = (v: number) =>
  v > 0 ? "text-success" : v < 0 ? "text-destructive" : "text-muted-foreground";

export const TokenCard = ({
  symbol,
  name,
  rank,
  price,
  priceCaption,
  score,
  rating,
  ratingClassName,
  badges,
  tags,
  changes,
  stats,
  reasoning,
  children,
  footer,
  headerRight,
  pinnable = true,
  hero = false,
  className,
  onClick,
}: TokenCardProps) => {
  const validScore = score != null && isFinite(score) && score > 0;

  return (
    <Card
      onClick={onClick}
      className={cn(
        "gradient-card border-border/50 hover:border-primary/50 transition-all",
        hero ? "p-6" : "p-4",
        onClick && "cursor-pointer",
        className,
      )}
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {rank != null && <span className="type-eyebrow text-muted-foreground">#{rank}</span>}
              <h3 className={cn("font-bold truncate", hero ? "text-xl" : "text-base")}>
                {name || symbol}
              </h3>
              {name && name !== symbol && (
                <span className="type-data-sm text-muted-foreground">{symbol}</span>
              )}
              {rating && (
                <Badge variant="outline" className={cn("text-xs", ratingClassName)}>
                  {rating}
                </Badge>
              )}
            </div>
            {(badges || (tags && tags.length > 0)) && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                {tags?.map((t) => (
                  <Badge key={t} variant="outline" className="text-[10px] py-0 px-1.5 border-primary/30 text-primary">
                    {t}
                  </Badge>
                ))}
                {badges}
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-1 shrink-0">
            {pinnable && <WatchlistPinButton symbol={symbol} />}
            {headerRight ?? (validScore ? <TossScoreOrb score={score!} size="sm" showLabel={false} /> : null)}
          </div>
        </div>

        {/* Price + headline changes */}
        {(price || (changes && changes.length > 0)) && (
          <div className="flex items-end justify-between gap-3">
            {price && (
              <div>
                <span className={cn("type-data block", hero ? "text-3xl" : "text-2xl")}>{price}</span>
                {priceCaption && <span className="type-caption">{priceCaption}</span>}
              </div>
            )}
            {changes && changes.length > 0 && (
              <div className="flex items-center gap-4 shrink-0">
                {changes.map((c) => (
                  <div key={c.label} className="text-right">
                    <div
                      className={cn(
                        "type-data-sm flex items-center gap-1 justify-end",
                        c.value == null ? "text-muted-foreground" : pctTone(c.value),
                      )}
                    >
                      {c.value != null && c.value !== 0 &&
                        (c.value > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />)}
                      {c.value == null ? "—" : `${c.value > 0 ? "+" : ""}${c.value.toFixed(2)}%`}
                    </div>
                    <div className="type-caption">{c.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Context-specific figures */}
        {stats && stats.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-1">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="type-eyebrow text-muted-foreground">{s.label}</div>
                <div className={cn("type-data-sm mt-0.5", s.tone ?? "text-foreground")}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {reasoning && <div className="text-sm text-muted-foreground leading-relaxed">{reasoning}</div>}

        {children}

        {footer && <div className="pt-1">{footer}</div>}
      </div>
    </Card>
  );
};

export default TokenCard;
